import React, { useEffect, useMemo, useState } from 'react';
import { subMonths } from 'date-fns';
import { Range } from 'react-date-range';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import useHeaderTitleContext from '../hooks/useHeaderTitleContext';
import useUserContext from '../hooks/useUserContext';
import FulfillmentPerformance from '../components/molecules/FulfillmentPerformance';
import ScStat from '../components/molecules/ScStat';
import ScSectionHeader from '../components/molecules/ScSectionHeader';
import ScDonut from '../components/molecules/ScDonut';
import type { DonutSegment } from '../components/molecules/ScDonut';
import ScSparkline from '../components/molecules/ScSparkline';
import ScCard from '../components/atoms/ScCard';
import Button from '../components/atoms/Button';
import routes from '../setup/consts/routes';
import { formatStat, useDashboardSummary } from './dashboard/hooks';
import {
  useAvailableBalance,
  usePendingBalance,
  useWithdrawnBalance,
} from './financials/hooks';
import { useCitiesList } from '../hooks/useCitiesList';
import useCookieValue from '../hooks/useCookieValue';
import { CLIENT_SELECT_VALUE_COOKIE_NAME } from '../components/layouts/LayoutBase/consts';
import { ClientSelectValueCookieShape } from '../utils/auth/types';
import {
  IconBox,
  IconCommand,
  IconFinance,
  IconLocation,
  IconPlus,
} from '../assets/svg/icons';
import ButtonLink from '../components/atoms/Button/buttonLink';
import { ButtonKind, ButtonSize } from '../components/atoms/Button/enums';
import SelectBase from '../components/atoms/Fields/Select/base';
import Datepicker from '../components/atoms/Fields/Datepicker';
import { DatepickerPosition } from '../components/atoms/Fields/Datepicker/enums';
import { useWarehouseProducts } from './warehouse/hooks';
import { GetWarehouseProductsResponseSingleItem } from '../setup/apiTypes/warehouse';
import {
  StyledBottomRow,
  StyledCityDot,
  StyledCityLegend,
  StyledCityLegendItem,
  StyledCityName,
  StyledCityPct,
  StyledFilterBar,
  StyledFilterCitySelect,
  StyledFilterLabel,
  StyledFinancialCard,
  StyledFinancialLabel,
  StyledFinancialSparkRow,
  StyledFinancialValue,
  StyledFinancialsGrid,
  StyledHeroActions,
  StyledHeroEyebrow,
  StyledHeroHint,
  StyledHeroSub,
  StyledHeroTitle,
  StyledDashboardHero,
  StyledKpiGrid,
  StyledTopSellingList,
  StyledTopSellingRow,
  StyledTopSellingIconTile,
  StyledTopSellingMeta,
  StyledTopSellingName,
  StyledTopSellingSku,
  StyledTopSellingUnits,
  StyledTopSellingCount,
  StyledTopSellingLabel,
  StyledTopSellingEmpty,
  StyledTopSellingPlaceholderRow,
  StyledSeeAllLink,
} from './dashboard/styles';

import { CHART_COLORS } from '../theme/brandColors';
const CITY_COLORS = CHART_COLORS;

const greetingKey = (hour: number): string => {
  if (hour < 5) return 'dashboard.greeting.workingLate';
  if (hour < 12) return 'dashboard.greeting.morning';
  if (hour < 17) return 'dashboard.greeting.afternoon';
  if (hour < 22) return 'dashboard.greeting.evening';
  return 'dashboard.greeting.workingLate';
};

const DashboardPage: React.FC = () => {
  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const history = useHistory();
  const { user } = useUserContext();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.dashboard',
        defaultMessage: 'Dashboard',
      })
    );
  }, [intl, setHeaderTitle]);

  const firstName = useMemo(() => {
    if (!user?.name) return undefined;
    return user.name.split(/\s+/)[0];
  }, [user?.name]);

  const greeting = useMemo(() => intl.formatMessage({ id: greetingKey(new Date().getHours()) }), [intl]);
  const todayLabel = useMemo(() => {
    const now = new Date();
    const locale = intl.locale === 'ar' ? 'ar-IQ' : 'en-GB';
    return now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'short' });
  }, [intl]);

  const [range, setRange] = useState<Range>({
    startDate: subMonths(new Date(), 1),
    endDate: new Date(),
  });
  const clientCookie = useCookieValue<ClientSelectValueCookieShape>(CLIENT_SELECT_VALUE_COOKIE_NAME);
  const selectedEntityIds = useMemo(() => clientCookie?.entities ?? [], [clientCookie]);

  const { list: cityOptions, isLoading: citiesLoading } = useCitiesList();
  const [city, setCity] = useState<string>('all');
  const cityOptionsWithAll = useMemo(
    () => [{ value: 'all', label: intl.formatMessage({ id: 'dashboard.allCities', defaultMessage: 'All cities' }) }, ...cityOptions],
    [cityOptions, intl]
  );
  const cityFilterValue = useMemo(() => {
    if (!city || city === 'all') return undefined;
    const match = cityOptions.find((o) => o.value === city);
    return (match?.label as string) ?? city;
  }, [city, cityOptions]);

  const summary = useDashboardSummary({
    city: cityFilterValue,
    from: range.startDate,
    to: range.endDate,
    entityIds: selectedEntityIds,
  });

  const raw = summary.raw;

  const revenueData = useMemo(() => raw?.dailyBreakdown.map((b) => b.revenue) ?? [], [raw]);
  const ordersData = useMemo(() => raw?.dailyBreakdown.map((b) => b.count) ?? [], [raw]);

  const cityLookup = useMemo(
    () => Object.fromEntries(cityOptions.map((o) => [o.value, o.label as string])),
    [cityOptions]
  );

  const citySegments = useMemo((): DonutSegment[] => {
    if (!raw || raw.cityBreakdown.length === 0) return [];
    return raw.cityBreakdown.slice(0, 8).map((c, i) => ({
      value: c.count,
      color: CITY_COLORS[i] ?? 'var(--muted-3)',
      label: cityLookup[c.city] ?? c.city,
    }));
  }, [raw, cityLookup]);

  const cityTotal = useMemo(
    () => citySegments.reduce((s, x) => s + x.value, 0),
    [citySegments]
  );

  const { availableBalance, currency: availableCurrency, isLoading: availableLoading } = useAvailableBalance();
  const { pendingBalance, currency: pendingCurrency, isLoading: pendingLoading } = usePendingBalance();
  const { withdrawnBalance, currency: withdrawnCurrency, isLoading: withdrawnLoading } = useWithdrawnBalance();

  const { items: rawWarehouseItems, isLoading: warehouseLoading } = useWarehouseProducts({ pageSize: 20 });
  const warehouseItems = rawWarehouseItems as unknown as GetWarehouseProductsResponseSingleItem[];

  const topSellingItems = useMemo(
    () => [...warehouseItems].sort((a, b) => (b.orderedQuantity ?? 0) - (a.orderedQuantity ?? 0)).slice(0, 3),
    [warehouseItems]
  );

  const returned = useMemo(() => {
    if (!raw) return 0;
    return raw.statusBreakdown
      .filter(({ status }) =>
        ['returned', 'return_pending', 'return_in_progress'].includes(status.toLowerCase())
      )
      .reduce((acc, { count }) => acc + count, 0);
  }, [raw]);

  return (
    <>
      {/* ── Dark gradient hero ── */}
      <StyledDashboardHero>
        <svg
          viewBox="0 0 200 200"
          style={{ position: 'absolute', right: -20, top: -20, width: 280, height: 280, opacity: 0.18, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <g fill="none" stroke="var(--brand-lime)" strokeWidth="1.5" strokeLinejoin="round">
            <path d="M40,80 L100,40 L160,80 L100,120 Z" />
            <path d="M40,80 L40,150 L100,190 L100,120" />
            <path d="M160,80 L160,150 L100,190" />
          </g>
        </svg>
        <div>
          <StyledHeroEyebrow>
            Sandoog Center{user?.accountDetails?.companyName ? ` · ${user.accountDetails.companyName}` : ''}
          </StyledHeroEyebrow>
          <StyledHeroTitle>
            {greeting}{firstName ? `, ${firstName}` : ''}
          </StyledHeroTitle>
          <StyledHeroSub>
            {intl.formatMessage({ id: 'dashboard.hero.sub', defaultMessage: "Here's where things stand across your orders, warehouse and customers — {todayLabel}." }, { todayLabel })}
          </StyledHeroSub>
          <StyledHeroActions style={{ marginTop: 'var(--s-5)' }}>
            <ButtonLink kind={ButtonKind.Primary} icon={IconPlus} to={routes.PLACE_ORDER}>
              <FormattedMessage id='dashboard.placeOrder' defaultMessage='Place an order' />
            </ButtonLink>
            <Button
              kind={ButtonKind.Outline}
              icon={IconCommand}
              onClick={() =>
                window.dispatchEvent(
                  new KeyboardEvent('keydown', { metaKey: true, key: 'k', bubbles: true })
                )
              }
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: 'var(--surface)',
                borderColor: 'rgba(255,255,255,0.18)',
              }}
            >
              {intl.formatMessage({ id: 'dashboard.quickSwitch', defaultMessage: 'Quick switch' })}{' '}
              <StyledHeroHint style={{ marginLeft: 4 }}>⌘K</StyledHeroHint>
            </Button>
          </StyledHeroActions>
        </div>
      </StyledDashboardHero>

      {/* ── Filters ── */}
      <StyledFilterBar>
        <StyledFilterLabel>{intl.formatMessage({ id: 'dashboard.filter', defaultMessage: 'Filter' })}</StyledFilterLabel>
        <StyledFilterCitySelect>
          <SelectBase
            placeholder={citiesLoading ? intl.formatMessage({ id: 'dashboard.loading', defaultMessage: 'Loading…' }) : intl.formatMessage({ id: 'dashboard.allCities', defaultMessage: 'All cities' })}
            icon={IconLocation}
            options={cityOptionsWithAll}
            notSeparated
            value={city}
            onChange={(v) => setCity(v ?? 'all')}
            withoutIndicatorRotation
            hideErrorMessageWrapper
          />
        </StyledFilterCitySelect>
        <div style={{ marginLeft: 'auto' }}>
          <Datepicker
            value={range}
            onChange={(item) => setRange(item)}
            position={DatepickerPosition.Right}
          />
        </div>
      </StyledFilterBar>

      {/* ── KPI strip ── */}
      <StyledKpiGrid>
        <ScStat
          label={intl.formatMessage({ id: 'dashboard.ordersToday', defaultMessage: 'Orders today' })}
          value={formatStat(summary.ordersToday)}
          sub={intl.formatMessage({ id: 'dashboard.ordersToday.sub', defaultMessage: 'Placed in last 24h' })}
          accent="green"
          isLoading={summary.isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'dashboard.pending', defaultMessage: 'Pending' })}
          value={formatStat(summary.pending)}
          sub={intl.formatMessage({ id: 'dashboard.pending.sub', defaultMessage: 'Awaiting verification' })}
          accent="ink"
          isLoading={summary.isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'dashboard.failedOrders', defaultMessage: 'Failed orders' })}
          value={formatStat(summary.failed7d)}
          sub={summary.failed7d.value > 0 ? intl.formatMessage({ id: 'dashboard.failedOrders.sub', defaultMessage: 'Review to recover revenue' }) : intl.formatMessage({ id: 'dashboard.failedOrders.clean', defaultMessage: 'Clean window' })}
          accent={summary.failed7d.value > 0 ? 'danger' : 'muted'}
          isLoading={summary.isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'dashboard.totalSkus', defaultMessage: 'Total SKUs' })}
          value={formatStat(summary.totalProducts)}
          sub={intl.formatMessage({ id: 'dashboard.totalSkus.sub', defaultMessage: 'In your warehouse' })}
          accent="ink"
          isLoading={summary.isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'dashboard.lowStock', defaultMessage: 'Low stock' })}
          value={formatStat(summary.lowStock)}
          sub={intl.formatMessage({ id: 'dashboard.lowStock.sub', defaultMessage: '≤ 5 units remaining' })}
          accent={summary.lowStock.value > 0 ? 'warning' : 'muted'}
          isLoading={summary.isLoading}
          showBorder
        />
      </StyledKpiGrid>

      {/* ── Fulfillment performance ── */}
      <div style={{ marginBottom: 'var(--s-6)' }}>
        <FulfillmentPerformance
          delivered={raw?.deliveredOrders ?? 0}
          inProgress={raw?.pendingOrders ?? 0}
          failed={raw?.failedOrders ?? 0}
          returned={returned}
          isLoading={summary.isLoading}
        />
      </div>

      {/* ── Financials snapshot ── */}
      <ScCard pad={false} style={{ padding: 'var(--s-5)', marginBottom: 'var(--s-6)' }}>
        <ScSectionHeader
          title={intl.formatMessage({ id: 'dashboard.financials', defaultMessage: 'Financials' })}
          subtitle={intl.formatMessage({ id: 'dashboard.financials.subtitle', defaultMessage: 'Last 30 days · IQD' })}
          icon={<IconFinance />}
          actions={
            <StyledSeeAllLink type="button" onClick={() => history.push(routes.FINANCIALS)}>
              {intl.formatMessage({ id: 'dashboard.seeAll', defaultMessage: 'See all →' })}
            </StyledSeeAllLink>
          }
        />
        <StyledFinancialsGrid>
          <StyledFinancialCard>
            <StyledFinancialLabel>{intl.formatMessage({ id: 'dashboard.currentBalance', defaultMessage: 'Current balance' })}</StyledFinancialLabel>
            <StyledFinancialValue>
              {availableLoading ? '…' : availableBalance.toLocaleString()}
              {' '}<span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{availableCurrency || 'IQD'}</span>
            </StyledFinancialValue>
            <StyledFinancialSparkRow>
              <span style={{ color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.vsPreviousPeriod', defaultMessage: 'vs last period —' })}</span>
              <ScSparkline data={revenueData} color="var(--brand-green)" width={120} height={36} />
            </StyledFinancialSparkRow>
          </StyledFinancialCard>

          <StyledFinancialCard>
            <StyledFinancialLabel>{intl.formatMessage({ id: 'dashboard.pendingBalance', defaultMessage: 'Pending balance' })}</StyledFinancialLabel>
            <StyledFinancialValue>
              {pendingLoading ? '…' : pendingBalance.toLocaleString()}
              {' '}<span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{pendingCurrency || 'IQD'}</span>
            </StyledFinancialValue>
            <StyledFinancialSparkRow>
              <span style={{ color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.vsPreviousPeriod', defaultMessage: 'vs last period —' })}</span>
              <ScSparkline data={ordersData} color="var(--info)" width={120} height={36} />
            </StyledFinancialSparkRow>
          </StyledFinancialCard>

          <StyledFinancialCard>
            <StyledFinancialLabel>{intl.formatMessage({ id: 'dashboard.withdrawn', defaultMessage: 'Withdrawn' })}</StyledFinancialLabel>
            <StyledFinancialValue>
              {withdrawnLoading ? '…' : withdrawnBalance.toLocaleString()}
              {' '}<span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{withdrawnCurrency || 'IQD'}</span>
            </StyledFinancialValue>
            <StyledFinancialSparkRow>
              <span style={{ color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.vsPreviousPeriod', defaultMessage: 'vs last period —' })}</span>
              <ScSparkline data={revenueData.map((v) => v * 0.65)} color="var(--warning)" width={120} height={36} />
            </StyledFinancialSparkRow>
          </StyledFinancialCard>
        </StyledFinancialsGrid>
      </ScCard>

      {/* ── Bottom row: city breakdown + attention ── */}
      <StyledBottomRow>
        {/* Sales by city */}
        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <ScSectionHeader
            title={intl.formatMessage({ id: 'dashboard.salesByCity', defaultMessage: 'Sales by city' })}
            subtitle={intl.formatMessage({ id: 'dashboard.salesByCity.subtitle', defaultMessage: 'Share of orders in period' })}
            icon={<IconLocation />}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-5)' }}>
            <ScDonut
              size={160}
              thickness={18}
              segments={citySegments.length > 0 ? citySegments : [{ value: 1, color: 'var(--paper-2)' }]}
              center={
                citySegments.length > 0 ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.topCity', defaultMessage: 'Top' })}</div>
                    <div style={{ fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}>
                      {citySegments[0]?.label ?? '—'}
                    </div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                      {cityTotal > 0 ? `${Math.round((citySegments[0]?.value ?? 0) / cityTotal * 100)}%` : '—'}
                    </div>
                  </div>
                ) : undefined
              }
            />
            <StyledCityLegend>
              {citySegments.slice(0, 8).map((seg, i) => (
                <StyledCityLegendItem key={seg.label ?? i}>
                  <StyledCityDot $color={seg.color} />
                  <StyledCityName>{seg.label ?? '—'}</StyledCityName>
                  <StyledCityPct>
                    {cityTotal > 0 ? `${Math.round(seg.value / cityTotal * 100)}%` : '—'}
                  </StyledCityPct>
                </StyledCityLegendItem>
              ))}
              {citySegments.length === 0 && !summary.isLoading && (
                <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', gridColumn: '1 / -1' }}>{intl.formatMessage({ id: 'dashboard.noCityData', defaultMessage: 'No city data available' })}</span>
              )}
            </StyledCityLegend>
          </div>
        </ScCard>

        {/* Top selling items */}
        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <ScSectionHeader
            title={intl.formatMessage({ id: 'dashboard.topSellingItems', defaultMessage: 'Top selling items' })}
            subtitle={intl.formatMessage({ id: 'dashboard.topSellingItems.subtitle', defaultMessage: 'By units moved, last 7 days' })}
            icon={<IconBox />}
            actions={
              <StyledSeeAllLink type="button" onClick={() => history.push(routes.WAREHOUSE)}>
                {intl.formatMessage({ id: 'dashboard.warehouseLink', defaultMessage: 'Warehouse →' })}
              </StyledSeeAllLink>
            }
          />
          {warehouseLoading ? (
            <StyledTopSellingList>
              <StyledTopSellingPlaceholderRow />
              <StyledTopSellingPlaceholderRow />
              <StyledTopSellingPlaceholderRow />
            </StyledTopSellingList>
          ) : topSellingItems.length === 0 ? (
            <StyledTopSellingEmpty>{intl.formatMessage({ id: 'dashboard.noData', defaultMessage: 'No data yet' })}</StyledTopSellingEmpty>
          ) : (
            <StyledTopSellingList>
              {topSellingItems.map((item) => (
                <StyledTopSellingRow key={item.productId}>
                  <StyledTopSellingIconTile>
                    <IconBox />
                  </StyledTopSellingIconTile>
                  <StyledTopSellingMeta>
                    <StyledTopSellingName className="ar">{item.productName}</StyledTopSellingName>
                    <StyledTopSellingSku className="mono">{item.sku}</StyledTopSellingSku>
                  </StyledTopSellingMeta>
                  <StyledTopSellingUnits>
                    <StyledTopSellingCount className="tnum">{(item.orderedQuantity ?? 0).toLocaleString()}</StyledTopSellingCount>
                    <StyledTopSellingLabel>{intl.formatMessage({ id: 'dashboard.unitsOrdered', defaultMessage: 'units ordered' })}</StyledTopSellingLabel>
                  </StyledTopSellingUnits>
                </StyledTopSellingRow>
              ))}
            </StyledTopSellingList>
          )}
        </ScCard>
      </StyledBottomRow>
    </>
  );
};

export default DashboardPage;
